/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX, WheelEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import CloseIcon from "../CloseIcon";
import type { HorizontalSelectionDialogProps } from "./HorizontalSelectionDialogProps";
import type { IOptionLabelParts } from "./HorizontalSelectionDialogFormatter";
import { splitOptionLabel } from "./HorizontalSelectionDialogFormatter";

import "./HorizontalSelectionDialog.scss";

/** Props for the tile label sub-component. */
interface IOptionLabelProps {
  /** Raw option label from the engine. */
  label: string;
  /** Whether the tile is rendered in the compact (single-size) style. */
  isCompact: boolean;
}

/**
 * Tile label. Compact tiles show the label as-is; the full-size tiles follow
 * Figma and set the leading number in the button type with its unit, smaller,
 * beneath it.
 */
const OptionLabel: FunctionComponent<IOptionLabelProps> = (
  props: IOptionLabelProps
): JSX.Element => {
  const { label, isCompact } = props;

  if (isCompact) {
    return <>{label}</>;
  }

  const parts: IOptionLabelParts = splitOptionLabel(label);

  if (parts.value === null) {
    return (
      <span className="horizontal-selection-dialog__option-label">{label}</span>
    );
  }

  return (
    <>
      <span className="horizontal-selection-dialog__option-value">
        {parts.value}
      </span>
      {parts.unit !== "" && (
        <span className="horizontal-selection-dialog__option-unit">
          {parts.unit}
        </span>
      )}
    </>
  );
};

/**
 * HorizontalSelectionDialog — reusable full-screen sub-panel that presents a
 * title, a description, and a horizontal row of selectable value boxes.
 *
 * Unity Design System › Dialog › Context=Select (Start Recording 2503:18535,
 * Keep At Most 2514:3767). Large tiles share the row equally; compact tiles
 * hug their labels and centre. Once a row outgrows the panel it becomes a
 * scroll container: D-pad navigation centres the focused tile (smooth where
 * supported), keeping a partial tile in view on each side so it stays obvious
 * that the row continues, and the mouse wheel scrolls the row directly. Both
 * TV and browser users can reach every option.
 *
 * Pointer input keeps its own highlight: hovering a tile lights it and stands
 * the D-pad highlight down until the next key press, so moving the pointer off
 * the row leaves nothing lit rather than exposing a focus the user never put
 * there. The row never scrolls in response, so tiles stay put under the cursor.
 */
const HorizontalSelectionDialog: FunctionComponent<
  HorizontalSelectionDialogProps
> = (props: HorizontalSelectionDialogProps): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09)
  const TAG: string = "HorizontalSelectionDialog";

  const {
    title,
    description,
    options,
    focusIndex,
    showCloseButton,
    onSelect,
    onCloseHover,
    onBack,
    isCompactOption
  } = props;

  /** Scroll viewport holding the option row. */
  const viewportRef = useRef<HTMLDivElement | null>(null);
  /** The currently focused tile, used to keep it inside the viewport. */
  const focusedOptionRef = useRef<HTMLButtonElement | null>(null);

  /**
   * Pointer highlight, as on the carousel: the tile under the pointer carries
   * the highlight for browser and LG cursor users, and `null` means nothing is
   * hovered. Deliberately local rather than pushed into the view model — hover
   * must not move the D-pad focus, since that would centre the tile and scroll
   * the row out from under the pointer.
   */
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  /**
   * Whether the pointer owns the row. Set once the pointer touches a tile and
   * held until a key press, so moving the pointer off the row leaves nothing
   * lit rather than exposing the D-pad focus the user never put there.
   */
  const [isPointerActive, setIsPointerActive] = useState<boolean>(false);

  // A key press hands the row back to the D-pad focus, dropping both the stale
  // highlight left wherever the pointer rests and the pointer's claim on it.
  useEffect((): (() => void) => {
    const releaseToDpad = (): void => {
      setHoveredIndex(null);
      setIsPointerActive(false);
    };
    window.addEventListener("keydown", releaseToDpad);
    return (): void => {
      window.removeEventListener("keydown", releaseToDpad);
    };
  }, []);

  useEffect((): void => {
    const container: HTMLDivElement | null = viewportRef.current;
    const option: HTMLButtonElement | null = focusedOptionRef.current;
    if (container === null || option === null) {
      return;
    }

    const maxScrollLeft: number = container.scrollWidth - container.clientWidth;
    if (maxScrollLeft <= 0) {
      return; // Row fits; nothing to scroll.
    }

    // Centre the focused tile rather than merely revealing it, so its
    // neighbours stay visible on both sides and the row reads as continuing
    // past the panel edges. `__options` is positioned, so it is the tile's
    // offsetParent and offsetLeft shares an origin with scrollLeft.
    const optionCentre: number = option.offsetLeft + option.offsetWidth / 2;
    const target: number = optionCentre - container.clientWidth / 2;

    // Clamped, so the first and last few tiles stop at the ends of the row
    // instead of centring against empty space.
    const nextScrollLeft: number = Math.max(0, Math.min(maxScrollLeft, target));

    if (nextScrollLeft !== container.scrollLeft) {
      // "smooth" animates on browsers and modern TV engines; older engines
      // ignore the option and perform an instant, still-correct scroll.
      container.scrollTo({ left: nextScrollLeft, behavior: "smooth" });
    }
  }, [focusIndex]);

  /**
   * Mouse-wheel scrolling. Adjusts scrollLeft directly so it works even when
   * native wheel scrolling is unreliable (embedded/TV engines) or when a parent
   * modal would otherwise swallow the event.
   */
  const handleWheel = useCallback((event: WheelEvent<HTMLDivElement>): void => {
    const container: HTMLDivElement | null = viewportRef.current;
    if (container === null) {
      return;
    }

    const maxScrollLeft: number = container.scrollWidth - container.clientWidth;
    if (maxScrollLeft <= 0) {
      return; // Row fits; nothing to scroll.
    }

    // A plain wheel only reports deltaY, so map it onto the row's axis;
    // trackpads and tilt wheels that report deltaX use it directly.
    const delta: number = event.deltaX !== 0 ? event.deltaX : event.deltaY;
    const nextScrollLeft: number = Math.max(
      0,
      Math.min(maxScrollLeft, container.scrollLeft + delta)
    );

    if (nextScrollLeft !== container.scrollLeft) {
      // Keep the gesture inside the row rather than bubbling to the overlay.
      event.stopPropagation();
      container.scrollLeft = nextScrollLeft;
    }
  }, []);

  return (
    <div
      className="horizontal-selection-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="horizontal-selection-dialog__scrim"
        onClick={onBack}
        aria-hidden="true"
      />

      <div className="horizontal-selection-dialog__panel">
        {showCloseButton === true && (
          <button
            type="button"
            aria-label="Close"
            className="horizontal-selection-dialog__close"
            onClick={onBack}
            onMouseEnter={onCloseHover}
          >
            <CloseIcon className="horizontal-selection-dialog__close-icon" />
          </button>
        )}

        <h2 className="horizontal-selection-dialog__title">{title}</h2>

        <p className="horizontal-selection-dialog__description">
          {description}
        </p>

        <div
          ref={viewportRef}
          className="horizontal-selection-dialog__options"
          onWheel={handleWheel}
        >
          <div
            className={
              "horizontal-selection-dialog__options-track" +
              (isCompactOption
                ? " horizontal-selection-dialog__options-track--compact"
                : "")
            }
            role="listbox"
            aria-label={title}
          >
            {options.map(
              (label: string, index: number): JSX.Element => (
                <button
                  key={label}
                  ref={index === focusIndex ? focusedOptionRef : undefined}
                  type="button"
                  role="option"
                  aria-selected={index === focusIndex}
                  className={
                    "horizontal-selection-dialog__option" +
                    (isCompactOption
                      ? " horizontal-selection-dialog__option--compact"
                      : "") +
                    (index === focusIndex && !isPointerActive
                      ? " horizontal-selection-dialog__option--focused"
                      : "") +
                    (index === hoveredIndex
                      ? " horizontal-selection-dialog__option--hovered"
                      : "")
                  }
                  onClick={(): void => onSelect(index)}
                  onMouseEnter={(): void => {
                    setHoveredIndex(index);
                    setIsPointerActive(true);
                  }}
                  onMouseLeave={(): void => setHoveredIndex(null)}
                >
                  <OptionLabel
                    label={label}
                    isCompact={isCompactOption === true}
                  />
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HorizontalSelectionDialog;
